// **Modelle auswählen statt eintippen** — für den Plattform-Key (Requesty) und
// den eigenen Endpoint des Kunden (jedes OpenAI-kompatible Gateway).
//
// Der Katalog kommt erst beim Aufklappen (`GET /tenants/:id/gateway-models`):
// Requesty nennt Preise je Modell, ein anderes Gateway oft nur die Kennungen —
// dann stehen keine Preise da. Er wird einmal je Seite geladen (gcTime 0: wer
// die Adresse ändert und wiederkommt, bekommt einen frischen).
//
// „Erlaubt" ist ein Filter im Picker der Instanz, keine Sperre am Key:
// Requesty kann einen Key nicht auf Modelle beschränken (docs/11 8d).

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { get } from "../../api/client";
import { keys } from "../../api/queries";
import type { GatewayModelsResponse } from "../../api/types";
import { fmtInt } from "../../lib/format";
import { Note } from "../../ui/badge";
import { Check } from "../../ui/field";
import { normalizeDefault } from "./models";
import { catalogErrorText, countText, ctxText, money, pickerRows, toggleModel, type PickerRow } from "./picker-rows";

export function ModelPicker({ tenantId, which, allowed, defaultModel, onChange }: {
  tenantId: number;
  which: "platform" | "own";
  allowed: string[];
  defaultModel: string;
  onChange: (allowed: string[], defaultModel: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [eu, setEu] = useState(false);
  const [only, setOnly] = useState(false);
  const catalog = useQuery({
    queryKey: keys.gatewayModels(tenantId, which),
    queryFn: () => get<GatewayModelsResponse>(`/tenants/${tenantId}/gateway-models?which=${encodeURIComponent(which)}`),
    enabled: open,
    staleTime: Infinity,
    gcTime: 0,
  });

  const setAllowed = (next: string[]) => onChange(next, normalizeDefault(next, defaultModel));

  return (
    <div className="pf-mp">
      <div className="pf-mp__summary">
        <div className="pf-field">
          <span className="pf-field__label">Erlaubte Modelle</span>
          <span className="pf-mp__count">{countText(allowed)}</span>
        </div>
        <div className="pf-field">
          <span className="pf-field__label">Standardmodell</span>
          <div className="pf-mp__default">
            {allowed.length ? (
              // Ein Menü aus genau den erlaubten — ein Standard außerhalb der
              // Liste stünde im Picker nicht zur Wahl.
              <div className="pf-mp__chips">
                {["", ...allowed].map((id) => (
                  <button key={id || "-"} type="button" className={`pf-chip${defaultModel === id ? " pf-chip--on" : ""}`} onClick={() => onChange(allowed, id)}>
                    {id || "erstes der Liste"}
                  </button>
                ))}
              </div>
            ) : (
              <input className="pf-input" defaultValue={defaultModel} placeholder="leer = erstes im Katalog" aria-label="Standardmodell" onChange={(e) => onChange(allowed, e.target.value.trim())} />
            )}
          </div>
        </div>
        <button type="button" className="pf-btn pf-btn--sm" onClick={() => setOpen((o) => !o)}>{open ? "Auswahl schließen" : "Modelle auswählen"}</button>
      </div>
      <div className="pf-mp__panel" hidden={!open}>
        <div className="pf-mp__tools">
          <input className="pf-input" type="search" placeholder="Suchen, z. B. gpt-5.6 oder claude" value={q} onChange={(e) => setQ(e.target.value)} />
          <Check checked={eu} onChange={setEu} label="nur EU-Regionen" />
          <Check checked={only} onChange={setOnly} label="nur ausgewählte" />
          <button type="button" className="pf-btn pf-btn--sm pf-btn--ghost" onClick={() => setAllowed([])}>Auswahl leeren</button>
        </div>
        <div className="pf-mp__list">
          {catalog.isError ? <Note tone="warn">{`Katalog nicht abrufbar: ${catalog.error instanceof Error ? catalog.error.message : String(catalog.error)}`}</Note>
            : !catalog.data ? <div className="pf-empty">Katalog wird geladen …</div>
              : catalog.data.error ? <Note tone="warn">{catalogErrorText(catalog.data.error, catalog.data.detail)}</Note>
                : <PickerList catalog={catalog.data} selected={allowed} filter={{ q, eu, only }} onToggle={(id, on) => setAllowed(toggleModel(allowed, id, on))} />}
        </div>
      </div>
    </div>
  );
}

function PickerList({ catalog, selected, filter, onToggle }: {
  catalog: GatewayModelsResponse;
  selected: string[];
  filter: { q: string; eu: boolean; only: boolean };
  onToggle: (id: string, on: boolean) => void;
}) {
  const { rows, more, priced } = pickerRows(catalog.models, selected, filter);
  if (!rows.length) return <div className="pf-empty">Kein Modell passt zu diesem Filter.</div>;
  const ids = new Set(selected);
  return (
    <>
      <table className="pf-table pf-table--compact pf-mp__table">
        <thead><tr><th></th><th>Modell</th>{priced && <><th className="pf-num">Ein / 1M</th><th className="pf-num">Aus / 1M</th></>}<th className="pf-num pf-hide-phone">Kontext</th></tr></thead>
        <tbody>{rows.map((m) => <PickerRowView key={m.id} m={m} on={ids.has(m.id)} priced={priced} onToggle={onToggle} />)}</tbody>
      </table>
      {more > 0 && <div className="pf-sub pf-mp__more">{fmtInt(more)} weitere — Suche eingrenzen</div>}
    </>
  );
}

function PickerRowView({ m, on, priced, onToggle }: { m: PickerRow; on: boolean; priced: boolean; onToggle: (id: string, on: boolean) => void }) {
  return (
    <tr>
      <td><input type="checkbox" checked={on} aria-label={m.id} onChange={(e) => onToggle(m.id, e.target.checked)} /></td>
      <td className="mono pf-break">
        {m.id}
        {m.missing && <> <span className="pf-tag pf-tag--warn">nicht im Katalog</span></>}
        {m.tools === false && <> <span className="pf-tag">ohne Werkzeuge</span></>}
      </td>
      {priced && <><td className="pf-num">{m.missing ? "—" : `$${money(m.input)}`}</td><td className="pf-num">{m.missing ? "—" : `$${money(m.output)}`}</td></>}
      <td className="pf-num pf-hide-phone">{m.context_window ? ctxText(m.context_window) : "—"}</td>
    </tr>
  );
}
